import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import styled from 'styled-components';

import { useGroupLessons } from 'core/hooks';
import { selectUpcomingLessons } from 'core/utils/groupLessons';
import { LoadingIndicator } from 'atoms';

import * as S from '../../../FakturowniaInvoices/FakturowniaInvoices.styled';
import { GroupColors } from '../groupColors';
import { OverviewNavTarget } from './types';

interface IUpcomingLessonsSectionProps {
  /** Group whose schedule feeds the section. */
  groupId: string;
  /**
   * Whether the group is finished. When `true` the whole section is hidden
   * (Req 2.2). Passed in by the OverviewTab (task 12.3) from
   * `group.statusFinished` so this part stays decoupled from the meta query.
   */
  statusFinished: boolean;
  /** Navigate to a sibling tab. Selecting a lesson maps to `'schedule'`. */
  onNavigate: (target: OverviewNavTarget) => void;
}

/** How many upcoming lessons to surface in the Overview snapshot. */
const UPCOMING_LIMIT = 5;

/**
 * UpcomingLessonsSection — Overview snapshot of the group's next lessons
 * (Req 2.1). Fed by `useGroupLessons(groupId)` and the pure
 * `selectUpcomingLessons(lessons, Date.now())`, so the list is exactly the
 * future lessons in chronological order.
 *
 * Hidden entirely when the group is finished (Req 2.2). Each lesson item is a
 * keyboard-activatable control that navigates to the Schedule tab (Req 2.4)
 * via `onNavigate('schedule')`.
 *
 * States: loading (initial), empty (no upcoming lessons), populated. All copy
 * resolves through i18next under `pages.groupDetail.*`; all colors come from
 * the shared `GroupColors` theme tokens (no hardcoded hex).
 */
export const UpcomingLessonsSection = memo(
  ({ groupId, statusFinished, onNavigate }: IUpcomingLessonsSectionProps) => {
    const { t } = useTranslation();
    const { group, loading } = useGroupLessons(
      statusFinished ? undefined : groupId,
    );

    const upcoming = useMemo(() => {
      const lessons = group?.lessons || [];
      return selectUpcomingLessons(lessons, Date.now()).slice(
        0,
        UPCOMING_LIMIT,
      );
    }, [group?.lessons]);

    // Req 2.2 — the section does not exist for a finished group.
    if (statusFinished) {
      return null;
    }

    return (
      <S.Card>
        <S.CardTitle>
          {t('pages.groupDetail.overview.upcomingLessons.title')}
        </S.CardTitle>

        {loading && upcoming.length === 0 ? (
          <Center>
            <LoadingIndicator />
          </Center>
        ) : upcoming.length === 0 ? (
          <EmptyMessage>
            {t('pages.groupDetail.overview.upcomingLessons.empty')}
          </EmptyMessage>
        ) : (
          <List>
            {upcoming.map((lesson) => {
              const when = lesson.date
                ? moment(lesson.date).format('DD.MM.YYYY')
                : '—';
              const time = lesson.startTime
                ? `${lesson.startTime}${
                    lesson.endTime ? `–${lesson.endTime}` : ''
                  }`
                : '';
              return (
                <LessonItem
                  key={lesson.id}
                  type="button"
                  onClick={() => onNavigate('schedule')}
                >
                  <LessonName>{lesson.name || '—'}</LessonName>
                  <LessonWhen>
                    {when}
                    {time ? ` · ${time}` : ''}
                  </LessonWhen>
                </LessonItem>
              );
            })}
          </List>
        )}
      </S.Card>
    );
  },
);

const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: 16px;
`;

const EmptyMessage = styled.div`
  color: ${GroupColors.textMuted};
  font-size: 13px;
  font-weight: 500;
  padding: 8px 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const LessonItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  text-align: left;
  border: 1px solid ${GroupColors.border};
  background: ${GroupColors.surface};
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;

  &:hover,
  &:focus-visible {
    background: ${GroupColors.surfaceSubtle};
    border-color: ${GroupColors.accent};
    outline: none;
  }
`;

const LessonName = styled.span`
  color: ${GroupColors.textPrimary};
  font-size: 13px;
  font-weight: 600;
`;

const LessonWhen = styled.span`
  color: ${GroupColors.textMuted};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;
